import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';

export interface UserPreferences {
  timezone: string;          // IANA tz e.g. "America/New_York"; "auto" = browser default
  dateFormat: '12h' | '24h';
}

interface UserPreferencesContextValue {
  preferences: UserPreferences;
  updatePreferences: (partial: Partial<UserPreferences>) => void;
  effectiveTimezone: string;
}

const STORAGE_KEY = 'nuance-user-preferences';

const defaultPreferences: UserPreferences = {
  timezone: 'auto',
  dateFormat: '12h',
};

function loadPreferences(): UserPreferences {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return { ...defaultPreferences, ...JSON.parse(raw) };
  } catch { /* ignore */ }
  return defaultPreferences;
}

const UserPreferencesContext = createContext<UserPreferencesContextValue | null>(null);

export function UserPreferencesProvider({ children }: { children: React.ReactNode }) {
  const [preferences, setPreferences] = useState<UserPreferences>(loadPreferences);

  const updatePreferences = useCallback((partial: Partial<UserPreferences>) => {
    setPreferences((prev) => {
      const next = { ...prev, ...partial };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  const effectiveTimezone = useMemo(
    () =>
      preferences.timezone === 'auto'
        ? Intl.DateTimeFormat().resolvedOptions().timeZone
        : preferences.timezone,
    [preferences.timezone],
  );

  return (
    <UserPreferencesContext.Provider value={{ preferences, updatePreferences, effectiveTimezone }}>
      {children}
    </UserPreferencesContext.Provider>
  );
}

export function useUserPreferences() {
  const ctx = useContext(UserPreferencesContext);
  if (!ctx) throw new Error('useUserPreferences must be used within UserPreferencesProvider');
  return ctx;
}
